
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  CssBaseline, 
  Container,
  Typography, 
  Fade
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { SectionProvider } from '../../contexts/SectionContext';
import Header from './Header';
import MobileDrawer from './MobileDrawer';
// import RouteProgress from '../RouteProgress'; // Removed due to performance issues

interface MainLayoutProps {
  showFooter?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ showFooter = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  useThemeContext();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  // Track whether the initial auth check has completed.
  // Once it has, we never show the full-page loading overlay again
  // (login/logout loading is handled by the AuthModal / individual pages).
  const authInitializedRef = useRef(false);
  const [showInitialLoading, setShowInitialLoading] = useState(true);

  useEffect(() => {
    if (!loading && !authInitializedRef.current) {
      authInitializedRef.current = true;
      setShowInitialLoading(false);
    }
  }, [loading]);
  
  // Simulate page transition loading
  useEffect(() => {
    setPageLoading(true);
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Check if we're trying to access admin routes
  if (location.pathname.startsWith('/admin')) {
    if (loading) {
      return null; // Wait for auth to load before checking
    }
    if (!user || !user.isAdmin) {
      return <Navigate to="/" state={{ from: location }} replace />;
    }
  }

  // Show a simple non-overlay loading screen only during the initial auth check
  if (showInitialLoading && loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          gap: 2,
        }}
      >
        <CircularProgress aria-label="Loading your account" />
        <Typography variant="h6" color="text.secondary">
          Loading your account...
        </Typography>
      </Box>
    );
  }

  return (
    <>
    <SectionProvider>
      <CssBaseline />
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          transition: 'background-color 0.3s ease, color 0.3s ease'
        }}
      >
        <Header 
          mobileDrawerOpen={mobileDrawerOpen} 
          setMobileDrawerOpen={setMobileDrawerOpen} 
        />
        <MobileDrawer
          open={mobileDrawerOpen} 
          onClose={() => setMobileDrawerOpen(false)} 
        />
        
        {/* Page loading indicator - disabled due to performance issues */}
        {/* <RouteProgress loading={pageLoading} /> */}
        
        <Container 
          component="main" 
          role="main"
          aria-live="polite"
          sx={{ 
            flexGrow: 1, 
            maxWidth: { xs: '100%', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' },
            px: { xs: 0, sm: 2, md: 3 },
            transition: 'padding 0.2s ease',
            pt: { xs: '96px', sm: '106px', md: '64px' }, // Header toolbar + SectionTabs on mobile
            width: '100%'
          }}>
          {/* Mobile Section Tabs moved into Header AppBar — see Header.tsx */}
          <Box sx={{ 
            mt: { xs: 0, sm: 1, md: 2 },
            px: { xs: 2, sm: 0 }
          }}> {/* Add responsive spacing */}
            <Fade in={!pageLoading} timeout={300}>
              <Box>
                <Outlet />
              </Box>
            </Fade>
          </Box>
        </Container>
        
        {/* Footer */}
        {showFooter && (
          <Box 
            component="footer" 
            role="contentinfo"
            sx={{ 
              py: 3, 
              px: 2, 
              mt: 'auto',
              backgroundColor: (theme) => theme.palette.mode === 'light' 
                ? theme.palette.grey[200] 
                : theme.palette.grey[900],
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Container maxWidth="lg">
              <Typography 
                variant="body2" 
                color="text.secondary" 
                align="center"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                © {new Date().getFullYear()} Social Media App
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                align="center" 
                sx={{ 
                  display: 'block', 
                  mt: 1,
                  fontSize: { xs: '0.625rem', sm: '0.75rem' },
                  px: { xs: 2, sm: 0 }
                }}
              >
                Connect and share with people who matter
              </Typography>
            </Container>
          </Box>
        )}
      </Box>
    </SectionProvider>
    </>
  );
};

export default MainLayout;

